import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '../ui/button';
import { logActivity } from '../../lib/activityLog';
import { SupportOrgList } from './SupportHint';

const STORAGE_KEY = 'precious_mylife_walkthrough_v1';

/**
 * Returns true if this user hasn't completed the walkthrough yet.
 * Used on page load to decide whether to auto-open it.
 */
export const shouldShowWalkthrough = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== '1';
  } catch {
    return true; // safe default — show the intro
  }
};

export const markWalkthroughSeen = () => {
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
};

export const resetWalkthrough = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
};

const WALKTHROUGH_ORGS = [
  {
    name: 'Become',
    description: 'The national charity for children in care and young care leavers. Free support line.',
    phone: '0800 023 2033',
    url: 'https://becomecharity.org.uk',
  },
  {
    name: 'Samaritans',
    description: 'Someone to listen, 24 hours a day, every day of the year.',
    phone: '116 123',
    url: 'https://www.samaritans.org',
  },
  {
    name: 'Coram Voice',
    description: 'Advice and advocacy for care-experienced young people.',
    phone: '0808 800 5792',
    url: 'https://coramvoice.org.uk',
  },
];

// -----------------------------------------------------------------------------
// Content for each step.
// -----------------------------------------------------------------------------
const StepHeading = ({ eyebrow, children }) => (
  <div className="space-y-2">
    <p className="pd-eyebrow">{eyebrow}</p>
    <h2 className="font-heading text-[26px] font-semibold tracking-[-0.02em] leading-[1.15] text-ink">
      {children}
    </h2>
  </div>
);

const Step1 = () => (
  <div className="space-y-5" data-testid="walkthrough-step-1">
    <StepHeading eyebrow="Step 1 of 3">Before you begin</StepHeading>
    <div className="space-y-3 text-[16px] leading-relaxed text-ink-70">
      <p>
        These are the records that councils, schools and other organisations kept about
        your childhood. Other people wrote them, at the time, for their own reasons.
      </p>
      <p>
        The tone can be cold. Some records may be wrong, or describe events differently
        from how you remember them. That does not mean your memory is wrong. You are
        reading someone else's version of your life.
      </p>
      <p>
        There is no right way to read them and no rush. This page will be here when you
        come back.
      </p>
    </div>
  </div>
);

const Step2 = () => (
  <div className="space-y-5" data-testid="walkthrough-step-2">
    <StepHeading eyebrow="Step 2 of 3">Have someone you can talk to</StepHeading>
    <p className="text-[16px] leading-relaxed text-ink-70">
      Reading these records can bring a lot up. Before you start, think about who you
      could talk to if you need to stop, and where you could take a break.
    </p>
    <div className="rounded-lg bg-care-wash p-4 space-y-3">
      <p className="pd-mono text-care-ink">People you can call</p>
      <SupportOrgList orgs={WALKTHROUGH_ORGS} />
    </div>
    <p className="text-sm text-ink-55">
      A friend, family member, GP, therapist or personal adviser also counts.
    </p>
  </div>
);

const VIEW_ROWS = [
  ['Timeline', 'Your records year by year. Stretches with no records are marked, because a gap is a finding too.'],
  ['Organisation', 'Everything each council, school or hospital held about you, grouped together.'],
  ['Placement', 'Records grouped under each home or placement you lived in.'],
  ['Person', 'The professionals and carers who appear across your records.'],
];

const Step3 = () => (
  <div className="space-y-5" data-testid="walkthrough-step-3">
    <StepHeading eyebrow="Step 3 of 3">What you will find here</StepHeading>
    <p className="text-[16px] leading-relaxed text-ink-70">
      Your records, shown four ways. Use whichever helps.
    </p>
    <ul className="space-y-2">
      {VIEW_ROWS.map(([title, text]) => (
        <li key={title} className="rounded-lg bg-ground px-4 py-3">
          <p className="font-semibold text-ink text-[15px]">{title}</p>
          <p className="text-sm text-ink-55 leading-relaxed">{text}</p>
        </li>
      ))}
    </ul>
    <p className="text-sm text-ink-55">
      Open any record to read the full text from that document.
    </p>
  </div>
);

// -----------------------------------------------------------------------------
// Main dialog
// -----------------------------------------------------------------------------
const STEPS = [Step1, Step2, Step3];

export const MyLifeWalkthrough = ({ open, onOpenChange, onReady }) => {
  const [step, setStep] = useState(0);
  const StepCmp = STEPS[step];
  const isLast = step === STEPS.length - 1;

  // Log when the walkthrough is first surfaced for this session.
  useEffect(() => {
    if (open) {
      const isFirstTime = (() => {
        try { return localStorage.getItem(STORAGE_KEY) !== '1'; } catch { return true; }
      })();
      logActivity('walkthrough_shown', { first_time: isFirstTime });
    }
  }, [open]);

  const handleReady = () => {
    markWalkthroughSeen();
    logActivity('walkthrough_completed');
    onReady?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto p-7 sm:p-8 rounded-xl"
        // Prevent accidental dismiss — user should intentionally tap 'I'm ready'.
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="mylife-walkthrough"
      >
        <VisuallyHidden>
          <DialogTitle>My life: before you begin</DialogTitle>
          <DialogDescription>An introduction before you read your records.</DialogDescription>
        </VisuallyHidden>

        <StepCmp />

        {/* Progress */}
        <div className="flex items-center gap-2 pt-4" data-testid="walkthrough-progress" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-[width,background-color] duration-state ${
                i === step ? 'w-6 bg-brand' : 'w-1.5 bg-rule'
              }`}
            />
          ))}
        </div>

        {/* Nav buttons */}
        <div className="flex items-center justify-between gap-3 pt-2">
          {step > 0 ? (
            <Button
              variant="ghost"
              onClick={() => setStep((s) => s - 1)}
              data-testid="walkthrough-back"
            >
              Back
            </Button>
          ) : (
            <span />
          )}
          {!isLast ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              data-testid="walkthrough-next"
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleReady}
              data-testid="walkthrough-ready"
            >
              I'm ready
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MyLifeWalkthrough;
